import { Injectable, OnInit } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { loadModules } from "esri-loader";
import esri = __esri;

import { WellSegmentModel } from "../../form-controls/components/new-casing-plan/models/casing-plan.model";
import { GISConfig, GISLayer } from "../models/gis-config.model";
import { FormationColor } from "../models/odnr-map-point.model";
import { GisTokenService } from "./gis-token.service";

@Injectable()
export class GisLayerService implements OnInit {
    casingPlanAPI: string = "/api/CasingPlan";
    config: GISConfig;

    // 2D Layers
    transportationLyr: esri.TileLayer;
    waterWellLyr: esri.MapImageLayer;
    oilGasWellsLyr: esri.MapImageLayer;
    minesLyr: esri.MapImageLayer;
    _2DPointLayer: esri.GraphicsLayer;

    //layers for Area of Review Map
    _aorBereaLayer: esri.GraphicsLayer;
    _aorBigInjunLayer: esri.GraphicsLayer;
    _aorDefaultLayer: esri.GraphicsLayer;
    _aorKeenerLayer: esri.GraphicsLayer;
    _aorProposedLayer: esri.GraphicsLayer;
    _aorShaleLayer: esri.GraphicsLayer;
    _aorSquawLayer: esri.GraphicsLayer;

    // Graphics layer for drawing 3D
    polylineLyr: esri.GraphicsLayer;
    bufferLyr: esri.GraphicsLayer;

    // Scene layers
    wellbores_3d_ag_lyr: esri.SceneLayer;
    wellbores_3d_bg_lyr: esri.SceneLayer;

    allTileLayers: esri.TileLayer[] = [];
    allMapImageLayers: esri.MapImageLayer[] = [];
    allGraphicsLayers: esri.GraphicsLayer[] = [];

    constructor(
        public readonly http: HttpClient,
        public readonly tokenService: GisTokenService
    ) {
        this.config = window["GIS.Conf"] as GISConfig;
    }

    ngOnInit() { }

    // Find the layer and toggle visibility
    toggleLayer(layerId: string): void {
        if (this.allTileLayers.find(x => x.id.toLocaleLowerCase() === layerId.toLowerCase())) {
            (this.allTileLayers.find(x => x.id.toLocaleLowerCase() === layerId.toLowerCase())).visible =
                !(this.allTileLayers.find(x => x.id.toLocaleLowerCase() === layerId.toLowerCase())).visible;
        } else if (this.allMapImageLayers.find(x => x.id.toLocaleLowerCase() === layerId.toLowerCase())) {
            (this.allMapImageLayers.find(x => x.id.toLocaleLowerCase() === layerId.toLowerCase())).visible =
                !(this.allMapImageLayers.find(x => x.id.toLocaleLowerCase() === layerId.toLowerCase())).visible;
        }
    }

    // Add Point graphic to 2D view
    async add2DPoint(longitude: number, latitude: number, title: string, map: esri.Map, pointType: number = PointType.CasingPlan): Promise<void> {
        if ((longitude < 180 || longitude > -180) && (latitude > -180 || latitude < 180)) {
            var error;
            try {
                const [Graphic, GraphicsLayer] = await loadModules([
                    "esri/Graphic",
                    "esri/layers/GraphicsLayer"
                ]);

                const point = {
                    type: "point",
                    longitude: longitude,
                    latitude: latitude
                };

                let markerSymbol = {};
                if (pointType === PointType.CasingPlan) {
                    markerSymbol = {
                        type: "simple-marker",
                        color: [226, 119, 40],
                        outline: {
                            color: [226, 119, 40],
                            width: 2
                        }
                    };
                } else if (pointType === PointType.ApprovedPermit) {
                    markerSymbol = {
                        type: "simple-marker",
                        color: "lightblue",
                        size: 18,
                        outline: {
                            color: "lightblue",
                            width: 2
                        }
                    };
                } else if (pointType === PointType.PendingPermit) {
                    markerSymbol = {
                        type: "simple-marker",
                        color: "lightgreen",
                        size: 18,
                        outline: {
                            color: "lightgreen",
                            width: 2
                        }
                    };
                }
                const pointGraphic = new Graphic({
                    geometry: point,
                    symbol: markerSymbol
                });
                // Graphics layer for later
                this._2DPointLayer = new GraphicsLayer({
                    id: "proposedPointLayer",
                    name: "Well Head",
                    title: title
                });
                this._2DPointLayer.add(pointGraphic);

                if (map && map.initialized)
                    map.layers.add(this._2DPointLayer);

            } catch (error) {
                console.log(`We have an error: ${error}`);
            }
        }
    }

    // Add Buffer Graphic to 3D webScene
    async addBufferGraphics(wellSegment: WellSegmentModel, groundGeometry: any, bufferSize: number): Promise<void> {
        var error;
        try {
            const [Graphic] = await loadModules([
                "esri/Graphic"
            ]);
            // Construct Points array
            let tempArray = [];
            for (let j = 0; j < wellSegment.gisObjects.length; j++) {
                tempArray.push([
                    wellSegment.gisObjects[j].longitude,
                    wellSegment.gisObjects[j].latitude,
                    (groundGeometry as esri.Point).z - wellSegment.gisObjects[j].tvd
                ]);
            }
            const polyline = {
                type: "polyline",
                paths: tempArray
            };
            const bufferSymbol = {
                type: "line-3d",
                symbolLayers: [{
                    type: "path",
                    size: bufferSize,
                    material: {
                        color: [51, 204, 51, 0.2]
                    }
                }]
            };
            const bufferGraphic = new Graphic({
                geometry: polyline,
                symbol: bufferSymbol
            });
            this.bufferLyr.removeAll();
            this.bufferLyr.add(bufferGraphic);
        } catch (error) {
            console.log(`We have an error: ${error}`);
        }
    }

    async addLineGraphics(wellSegments: WellSegmentModel[], map: esri.SceneView): Promise<void> {
        const [Graphic, Polyline, Point] = await loadModules([
            "esri/Graphic",
            "esri/geometry/Polyline",
            "esri/geometry/Point"
        ]);

        this.polylineLyr.graphics.removeAll();
        for (let i = 0; i < wellSegments.length; i++) {
            // Line Attributes
            const polyLineAttributes = {
                casingType: wellSegments[i].casingType.definition || "",
                boreholeDiameter: wellSegments[i].boreholeDiameter || "",
                boreholeDepth: wellSegments[i].boreholeBottom - wellSegments[i].boreholeTop || "",
                casingDiameter: wellSegments[i].casingDiameter || "",
                casingLength: wellSegments[i].casingBottom - wellSegments[i].casingTop || "",
                casingGrade: wellSegments[i].casingGrade || "",
                casingWeight: wellSegments[i].casingGrade || "",
                numberCentralizers: wellSegments[i].numberCentralizers || "",
                casingShoeDepth: wellSegments[i].casingShoeDepth || "",
                floatCollarDepth: wellSegments[i].floatCollarDepth || "",
                basketDepth: wellSegments[i].basketDepth || "",
                packerDepth: wellSegments[i].packerDepth || "",
                dvToolDepth: wellSegments[i].dvToolDepth || "",
                cementClass: wellSegments[i].cementClass || "",
                cementVolume: wellSegments[i].cementVolume || "",
                slurryYield: wellSegments[i].slurryYield || "",
            };

            for (var property in polyLineAttributes) {
                if (!polyLineAttributes[property] || typeof polyLineAttributes === "undefined" || polyLineAttributes[property] === "undefined")
                    delete polyLineAttributes[property];
            }

            // Some may come in without a Casing diameter (according to documentation) - use Borehole Diameter
            const width = wellSegments[i].casingDiameter > 0
                ? wellSegments[i].casingDiameter
                : wellSegments[i].boreholeDiameter;
            const lineSymbol = {
                type: "line-3d",
                symbolLayers: [
                    {
                        type: "path",
                        size: width / 2 > 0 ? width / 2 : 2, // points - Adjusting for scale... 2 is just a number that looked correct on the map
                        material: { color: this.getColor(wellSegments[i]) }
                    }
                ]
            };

            if (wellSegments[i].gisObjects.length < 2)
                continue;

            const startData = wellSegments[i].gisObjects.find(gis => gis.point === "Start");
            const endData = wellSegments[i].gisObjects.find(gis => gis.point === "End");

            if (!startData || !endData)
                continue;

            let startDepth = (startData.tvd) ? startData.tvd : startData.md;
            if (!startDepth) startDepth = 0;
            const start = new Point({
                x: startData.longitude,
                y: startData.latitude,
                hasZ: true,
                z: startDepth * -1
            });
            let endDepth = (endData.tvd) ? endData.tvd : endData.md;
            if (!endDepth) endDepth = 0;
            const end = new Point({
                x: endData.longitude,
                y: endData.latitude,
                hasZ: true,
                z: endDepth * -1
            }
            );
            const polyline = new Polyline({
                paths: [[start.longitude, start.latitude, start.z], [end.longitude, end.latitude, end.z]],
                hasZ: true
            });
            const kickPoints = wellSegments[i].gisObjects.filter(gis => gis.point === "Kick Point");
            for (let k = 0; k < kickPoints.length; k++) {
                let kickPointDepth = (kickPoints[k].tvd) ? kickPoints[k].tvd : kickPoints[k].md;
                if (!kickPointDepth) kickPointDepth = 0;
                const point = new Point(
                    {
                        y: kickPoints[k].latitude,
                        x: kickPoints[k].longitude,
                        hasZ: true,
                        z: (kickPoints[k].tvd) ? kickPoints[k].tvd * -1 : kickPointDepth * -1
                    });

                polyline.insertPoint(0, k + 1, point);
                if (!polyline.hasZ) polyline.hasZ = true;
            }

            const polylineGraphic = new Graphic({
                geometry: polyline,
                symbol: lineSymbol,
                attributes: polyLineAttributes
            });
            this.polylineLyr.add(polylineGraphic);
        }
    }

    // Remove all 3d lines from WebScene
    clearLineGraphics(): void {
        var error;
        try {
            this.polylineLyr.removeAll();
        } catch (error) {
            console.log(`We have an error: ${error}`);
        }
    }

    // Determine color for 3D line graphic
    getColor(wellSegment: WellSegmentModel): number[] {
        const code = (wellSegment.casingType && wellSegment.casingType.code) ? wellSegment.casingType.code.toLowerCase() : "";
        switch (code) {
            case "dp*":
                return [0, 220, 220];
            case "cond":
                return [220, 80, 0];
            case "cnd*":
                return [80, 220, 160];
            case "hol1":
                return [160, 0, 220];
            case "hol2":
                return [160, 10, 220];
            case "hol3":
                return [160, 20, 220];
            case "hol4":
                return [160, 30, 220];
            case "hol5":
                return [160, 40, 220];
            case "hol6":
                return [160, 50, 220];
            case "hol1*":
                return [160, 60, 220];
            case "hol2*":
                return [160, 70, 220];
            case "hol3*":
                return [160, 80, 220];
            case "hol4*":
                return [160, 90, 220];
            case "hol5*":
                return [160, 100, 220];
            case "hol6*":
                return [160, 110, 220];
            case "ms*":
                return [220, 0, 80];
            case "ms1":
                return [220, 0, 0];
            case "surf":
                return [80, 160, 160];
            case "srf*":
                return [0, 160, 220];
            case "i1":
                return [220, 0, 220];
            case "i1*":
                return [160, 80, 80];
            case "i2":
                return [220, 80, 80];
            case "i2*":
                return [220, 0, 0];
            case "prod":
                return [80, 80, 220];
            case "prd*":
                return [0, 220, 220];
            case "l1":
                return [160, 160, 160];
            case "l1*":
                return [80, 160, 160];
            case "t1":
            case "t2":
            case "t3":
            case "t4":
                return [160, 80, 80];
            default:
                return [220, 0, 220];
        }
    }

    async add3DLayers(map: esri.Map): Promise<any> {
        const [GraphicsLayer, FeatureLayer] = await loadModules([
            "esri/layers/GraphicsLayer",
            "esri/layers/FeatureLayer"
        ])

        let wellsFeatureLayer = new FeatureLayer({
            url: this.config.wellStringsUrl,
            title: 'Well Strings',
            id: 'wellStringsLyr'
        });

        this.polylineLyr = new GraphicsLayer();
        this.polylineLyr.id = "polylineLyr";
        this.bufferLyr = new GraphicsLayer();

        const elevationInfo: ElevationInfo = {
          mode: "relative-to-ground",
          offset: 0,
          unit: "feet"
        };

        this.allGraphicsLayers = [this.polylineLyr, this.bufferLyr, wellsFeatureLayer];
        this.allGraphicsLayers.forEach(x => { x.elevationInfo = elevationInfo });
        map.layers.removeAll();
        map.layers.addMany(this.allGraphicsLayers);
    }

    async add2DLayers(map: esri.Map): Promise<void> {
        map.layers.removeAll();

        this.tokenService.getToken().then(async token => {
            const [FeatureLayer, IdentityManager, esriRequest, TileLayer, MapImageLayer] = await loadModules([
                "esri/layers/FeatureLayer",
                "esri/identity/IdentityManager",
                "esri/request",
                "esri/layers/TileLayer",
                "esri/layers/MapImageLayer"
            ]);
            this.transportationLyr = null;

            //only load road name layer iff we are in satelite mode
            if (map.basemap.title === "Imagery")
                this.transportationLyr = new TileLayer({
                    url: this.config.transportationUrl,
                    id: "transportationLyr",
                    title: "Roadways",
                    opacity: 0.7
                });

            // Map Image Layers
            this.waterWellLyr = new MapImageLayer({
                url: this.config.waterWellsUrl,
                title: 'Water Wells',
                id: "waterWellsLyr"
            });
            this.oilGasWellsLyr = new MapImageLayer({
                url: this.config.oilGasWellsUrl,
                id: "oilGasWellsLyr",
                title: "Oil And Gas Wells"
            });
            this.minesLyr = new MapImageLayer({
                url: this.config.minesUrl,
                title: 'Mines',
                id: "minesLyr"
            });

            this.allTileLayers = [];
            this.allMapImageLayers = [this.waterWellLyr, this.oilGasWellsLyr, this.minesLyr];

            if (this.transportationLyr)
                this.allTileLayers.push(this.transportationLyr);

            map.layers.addMany(this.allTileLayers);
            map.layers.addMany(this.allMapImageLayers);

            const mapServerUrl =
                "https://gis.rics-jmt.net/arcgisportal/rest/services/Biweekly_Data/FeatureServer";
            esriRequest(mapServerUrl + "?f=json&token=" + token,
                {
                    responseType: "json",
                    token: token
                }).then(results => {
                    for (let i = 0; i < results.data.layers.length; i++) {
                        const tempFeatureLayer = new FeatureLayer({
                            url: mapServerUrl + "/" + i.toString(),
                            token: token,
                            id: i.toString()
                        });

                        map.layers.add(tempFeatureLayer);
                    }
                });
        });
    }

    async add2DFeatureLayers(map: esri.Map, mapServerUrl: string): Promise<any> {
        var error;
        try {
            this.tokenService.getToken().then(async token => {
                const [FeatureLayer, IdentityManager, esriRequest, TileLayer, MapImageLayer] = await loadModules([
                    "esri/layers/FeatureLayer",
                    "esri/identity/IdentityManager",
                    "esri/request",
                    "esri/layers/TileLayer",
                    "esri/layers/MapImageLayer"
                ]);

                let formattedMapServerUrl = mapServerUrl + "?f=json";

                if (map) {
                    esriRequest(formattedMapServerUrl).then(results => {
                        for (let i = 0; i < results.data.layers.length; i++) {
                            let layerUrl = mapServerUrl + "/" + results.data.layers[i].id.toString();
                            let tempFeatureLayer = new FeatureLayer({
                                url: layerUrl
                            });
                            map.add(tempFeatureLayer);
                        }
                    });
                }
            });
        } catch (error) {
            console.log(`We have an error: ${error}`);
        }
    }

    async getGeometryAtPoint(longitude: number, latitude: number, index: number): Promise<any> {
        var error;
        try {
            const [QueryTask, Query, Point] = await loadModules([
                "esri/tasks/QueryTask",
                "esri/tasks/support/Query",
                "esri/geometry/Point"
            ]);
            const minePoint = new Point({
                longitude: longitude,
                latitude: latitude
            });

            var queryTask = new QueryTask({
                url: `${this.config.minesUrl}/${index}`
            });
            var query = new Query();
            query.returnGeometry = false;
            query.where = "1 = 1";
            query.geometry = minePoint;
            query.outFields = "*";
            return queryTask.execute(query).then(results => {
                return results;
            });
        } catch (error) {
            console.log("error------.", error);
        }
    }

    // Data comes from an API outside our control, so keep the return value 'any' to guard against changes to the API
    getLayerData(layer: GISLayer): Promise<any> {
        return this.http.get<any>(`${this.casingPlanAPI}/GetLayerData/${layer}`).toPromise().then(results => {
            return results;
        }, (error) => {
            console.log("Error in getLayerData():", error);
        });
    }
}

export const PointType = ({
    "CasingPlan": 0,
    "ApprovedPermit": 1,
    "PendingPermit": 2,
    "AreaOfReviewPoint": 3
});

interface ElevationInfo {
  mode: "relative-to-ground" | "on-the-ground" | "absolute-height" | "relative-to-scene";
  offset: number;
  unit: "feet" | "meters" | "kilometers" | "miles" | "us-feet" | "yards";
};
